import { ReactNode, useEffect, useId, useRef, useState } from "react";
import styles from "./optionBlock.module.css";
import Tooltip from "@mui/material/Tooltip";
import { Box } from "@mui/material";
import { CoreChip, CoreChipProps, CoreSwitch } from "../../core";
import { useMobileLayout } from "../../hooks";
import { ProctoringPlatforms } from "@verity/proctoring-option/types";
import i18nPackage from "../../i18n/i18n";

export type OptionBlockProps = {
	name?: string;
	description?: ReactNode;
	contentHeader: string;
	Icon?: React.ElementType;
	disabled?: boolean;
	disabledMessage?: string;
	/** Will not be clickable or show disabled message even when checked. */
	fullDisabled?: boolean;
	checked: boolean;
	forceEnabling?: boolean;
	setForceEnabling?: (forceEnabling: boolean) => void;
	forceEnablingMessage?: string;
	updateState?: (state: boolean) => void;
	menuItems?: React.ReactNode;
	/** The width of the toggle under which the icon will be hidden. */
	compactWidth?: number;
	showIconOnCompact?: boolean;
	justifyContent?: string;
	modifiedFontSize?: string | number;
	/** If `false`, the menu action will not appear when the toggle is disabled. */
	disabledWithAction?: boolean;
	/** If `true`, the description will be in between the title and toggle switch */
	horizontalOrientation?: boolean;
	partiallySupportedPlatforms?: Set<keyof ProctoringPlatforms>;
};

export function OptionBlockContainer({
	forceEnabling,
	disabled,
	disabledMessage,
	horizontalOrientation,
	compactWidth,
	children,
}: Pick<
	OptionBlockProps,
	"forceEnabling" | "disabled" | "disabledMessage" | "horizontalOrientation" | "compactWidth"
> & {
	children: ReactNode;
}) {
	const [compacted, setCompacted] = useState(false);
	const wrapperRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		if (!wrapperRef.current || compactWidth === undefined) {
			return;
		}

		const resizeObserver = new ResizeObserver(() => {
			setTimeout(() => {
				if (wrapperRef.current?.clientWidth && wrapperRef.current.clientWidth < compactWidth) {
					if (!compacted) {
						setCompacted(true);
					}
				} else if (compacted) {
					setCompacted(false);
				}
			}, 0);
		});
		resizeObserver.observe(wrapperRef.current);
		return function cleanup() {
			resizeObserver.disconnect();
		};
	}, [compacted, compactWidth]);

	return (
		<div
			className={`${!horizontalOrientation ? styles.optionBoxWrapper : styles.optionBoxWrapperHorizontal} ${forceEnabling ? styles.shake : ""}`}
			style={{ position: "relative" }}
			ref={wrapperRef}
		>
			{children}
			{disabled && (
				<Tooltip
					title={
						<div className={styles.infoTextContainer}>
							<div className={styles.infoText}>{disabled && disabledMessage}</div>
						</div>
					}
					arrow
					PopperProps={{
						modifiers: [
							{
								name: "arrow",
								options: {
									padding: 0,
								},
							},
							{
								name: "offset",
								options: {
									offset: [0, 10],
								},
							},
							{
								name: "preventOverflow",
								options: {
									padding: 8,
								},
							},
							{
								name: "computeStyles",
								options: {
									adaptive: false,
								},
							},
							{
								name: "arrowCenterFix",
								enabled: true,
								phase: "write",
								fn({ state }) {
									const arrow = state.elements.arrow;
									if (arrow) {
										arrow.style.left = "50%";
										arrow.style.transform = "translateX(-50%)";
									}
								},
							},
						],
					}}
				>
					<div
						style={{
							top: 0,
							left: 0,
							position: "absolute",
							height: "110%",
							width: "100%",
							zIndex: 0,
						}}
					/>
				</Tooltip>
			)}
		</div>
	);
}

export function OptionBlockInfo({
	contentHeader,
	description,
	Icon,
	disabled,
	fullDisabled,
	id,
	partiallySupportedPlatforms,
}: Pick<
	OptionBlockProps,
	"contentHeader" | "description" | "Icon" | "disabled" | "fullDisabled" | "partiallySupportedPlatforms"
> & {
	id?: string;
}) {
	const isMobileLayout = useMobileLayout();
	return (
		<div className={`${styles.infoArea} ${isMobileLayout ? styles.infoAreaCompact : ""}`}>
			<div className={styles.titleArea}>
				<Box
					sx={{
						[`& .${styles.iconCenter} svg path`]: {
							fill: disabled || fullDisabled ? "#9883bd" : "#42296e",
						},
					}}
				>
					<div
						className={`
										${styles.iconCenter}
										${disabled || fullDisabled ? "disabled" : ""}
									`}
					>
						{Icon && <Icon aria-hidden="true" />}
					</div>
				</Box>
				<div className={`${styles.optionTitleText} ${disabled ? styles.disabledColor : ""}`} id={id}>
					{contentHeader}
					{Array.from(partiallySupportedPlatforms ?? []).map((platform) => (
						<OptionBlockPlatformBadge key={platform} platform={platform} />
					))}
				</div>
			</div>
			<div className={styles.optionDescriptionText}>{description}</div>
		</div>
	);
}

export function OptionBlockPlatformBadge({
	platform,
	chipProps,
}: {
	platform: keyof ProctoringPlatforms;
	chipProps?: Omit<CoreChipProps, "chipColor" | "children">;
}) {
	const badgeTextKey = platform === "isExtension" ? "extension" : platform === "isLockdown" ? "lockdown" : "mobile";
	const chipColor = platform === "isMobile" ? "#DFF2F2" : platform === "isLockdown" ? "#EAF6F6" : "#F4ECFA";
	return (
		<CoreChip
			chipColor={chipColor}
			variant="rectangular"
			className={styles.platformBadge}
			tooltip={i18nPackage.t(`editQuizPage.configurationStep.proctoringOptionsGridTSX.${badgeTextKey}.tooltip`)}
			{...chipProps}
		>
			{i18nPackage.t(`editQuizPage.configurationStep.proctoringOptionsGridTSX.${badgeTextKey}.badge`)}
		</CoreChip>
	);
}

export default function OptionBlock({
	description,
	contentHeader,
	Icon,
	disabled = false,
	disabledMessage,
	fullDisabled = false,
	checked,
	forceEnabling = false,
	setForceEnabling,
	menuItems,
	compactWidth = 225,
	updateState,
	horizontalOrientation,
	partiallySupportedPlatforms,
}: OptionBlockProps) {
	const headerId = useId();
	const isMobileLayout = useMobileLayout();

	return (
		<OptionBlockContainer
			forceEnabling={checked && forceEnabling}
			disabled={disabled}
			disabledMessage={disabledMessage}
			horizontalOrientation={horizontalOrientation}
			compactWidth={compactWidth}
		>
			{!horizontalOrientation ? (
				<div className={`${styles.optionBlock} ${isMobileLayout ? styles.optionBlockCompact : ""}`}>
					<OptionBlockInfo
						contentHeader={contentHeader}
						description={description}
						Icon={Icon}
						disabled={disabled}
						fullDisabled={fullDisabled}
						id={headerId}
						partiallySupportedPlatforms={partiallySupportedPlatforms}
					/>
					<div className={`${styles.switchArea} ${isMobileLayout ? styles.switchAreaCompact : ""}`}>
						<CoreSwitch
							checked={checked}
							onChange={(event) => {
								const newChecked = event.target.checked;
								if (updateState && !disabled) {
									updateState(newChecked);
									setForceEnabling?.(false);
								}
							}}
							disabled={fullDisabled || (disabled && !checked)}
							inputProps={{ "aria-labelledby": headerId }}
							labelTextType={isMobileLayout ? "none" : "left"}
						/>
					</div>
				</div>
			) : (
				<div className={`${styles.optionBlockHorizontal} ${isMobileLayout ? styles.optionBlockCompact : ""}`}>
					<div className={styles.infoAreaHorizontal}>
						<div className={styles.titleAreaHorizontal}>
							<Box
								sx={{
									[`& .${styles.iconCenter} svg path`]: {
										fill: disabled || fullDisabled ? "#9883bd" : "#42296e",
									},
								}}
							>
								<div
									className={`
										${styles.iconCenter}
										${disabled || fullDisabled ? "disabled" : ""}
									`}
								>
									{Icon && <Icon aria-hidden="true" />}
								</div>
							</Box>
							<div
								className={`${styles.optionTitleTextHorizontal} ${disabled ? styles.disabledColor : ""}`}
								id={headerId}
							>
								{contentHeader}
							</div>
						</div>
					</div>
					<div className={styles.optionDescriptionTextHorizontal}>{description}</div>
					<div className={styles.switchAreaHorizontal}>
						<CoreSwitch
							checked={checked}
							onChange={(event) => {
								const newChecked = event.target.checked;
								if (updateState && !disabled) {
									updateState(newChecked);
									setForceEnabling?.(false);
								}
							}}
							disabled={fullDisabled || (disabled && !checked)}
							inputProps={{ "aria-labelledby": headerId }}
							labelTextType={"left"}
						/>
					</div>
				</div>
			)}
			{checked && <div className={`${styles.checked} ${isMobileLayout ? styles.checkedCompact : ""}`}>{menuItems}</div>}
		</OptionBlockContainer>
	);
}
